import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom-v5-compat';

import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  styled,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

const ListContainer = styled('ul', {
  listStyle: 'none',
  margin: 0,
  padding: '0 !important',
});

const ListItem = styled('li', {
  backgroundColor: '$palette-grey-50',
  color: '$palette-indigo-600',
  cursor: 'pointer',
  padding: '$small $medium',
  position: 'absolute',
  transition: 'opacity 200ms ease-out',
  width: '100%',
});

const root = {
  position: 'relative',
  width: '100%',
  '@media (min-width: 720px)': {
    maxWidth: '588px',
    backgroundColor: 'red !important',
    color: 'red !important',
  },
};

interface FormValues {
  query: string;
}

function DashboardSearchbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isFocus, setIsFocus] = useState(false);
  const { handleSubmit, watch, reset, setValue } = useForm<FormValues>();
  const queryValue = watch('query');
  const hasInputValue = !!queryValue;

  const isOpen = isFocus && hasInputValue;

  const handleClearQuery = () => {
    reset({ query: '' });
  };

  const onSubmit = ({ query }: FormValues) => {
    if (query) {
      const searchText = /^\d+$/.test(query) ? `!${query}` : query;
      navigate(
        `/results?filters=(listView:all-shipments,viewType:shipment,filter.text.searchText:${searchText})`,
      );
    }
  };

  return (
    <Box css={root}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        data-testid="dashboard-searchbar"
      >
        <TextField
          aria-label={t('core.searchShipmentsLabel')}
          placeholder={t('core.searchEntity', { entity: t('core.shipments') })}
          startIcon={<Icon icon="search" css={{ color: '$palette-black' }} />}
          inputProps={{ maxLength: 200 }}
          onChange={(nextValue) => setValue('query', nextValue)}
          onBlur={() => setIsFocus(false)}
          onFocus={() => setIsFocus(true)}
          endIcon={
            <IconButton
              data-testid="clear-button"
              variant="tertiary"
              onPress={handleClearQuery}
              type="reset"
              css={{
                height: 'fit-content',
                opacity: hasInputValue ? 1 : 0,
                padding: '0 !important',
                pointerEvents: 'auto',
                transition: `opacity 200ms ease-out`,
              }}
            >
              <Icon icon="close" />
            </IconButton>
          }
        />
      </form>
      {hasInputValue && (
        <ListContainer
          data-testid="list-options"
          css={{
            opacity: isOpen ? 1 : 0,
            transform: isOpen ? '' : 'scale(0)',
            transition: `opacity 200ms ease-out ${
              isOpen ? '' : ', transform 200ms ease 200ms'
            }`,
          }}
        >
          <ListItem
            role="button"
            tabIndex={0}
            onClick={() => onSubmit({ query: queryValue })}
          >
            <Flex align="center" justify="between">
              <Flex align="center" gap="x-small">
                <Icon icon="search" css={{ fontSize: '$large' }} />
                <Typography variant="subtextBold">
                  {t('core.viewAllEntity', { entity: t('core.results') })}
                </Typography>
              </Flex>
              <Button
                endIcon={<Icon icon="keyboard_return" />}
                role="presentation"
                size="small"
                css={{
                  backgroundColor: '$palette-grey-800',
                  pointerEvents: 'none',
                }}
              >
                {t('global.select')}
              </Button>
            </Flex>
          </ListItem>
        </ListContainer>
      )}
    </Box>
  );
}

export default DashboardSearchbar;
